import { SortOrder } from '@kirin/api-client/contracts/helpers';
import type { TableQueryState, TableQueryAction } from '../types';

export const initialTableQueryState: TableQueryState = {
  pagination: {
    pageIndex: 0,
    pageSize: 10,
  },
  filters: [],
  sort: null,
  search: '',
  expandedRowIds: new Set(),
};

/**
 * Centralized query state reducer with invariants:
 * - page size change resets to page 0
 * - page change preserves other state
 * - filter changes reset to page 0
 */
export function tableQueryReducer(
  state: TableQueryState,
  action: TableQueryAction
): TableQueryState {
  switch (action.type) {
    case 'SET_PAGE':
      return {
        ...state,
        pagination: { ...state.pagination, pageIndex: action.payload },
        expandedRowIds: new Set(), // Collapse all on page change
      };

    case 'SET_PAGE_SIZE':
      return {
        ...state,
        pagination: {
          pageIndex: 0, // Reset to first page
          pageSize: action.payload,
        },
        expandedRowIds: new Set(), // Collapse all on page size change
      };

    case 'ADD_FILTER':
      return {
        ...state,
        filters: [...state.filters, action.payload],
        pagination: {
          ...state.pagination,
          pageIndex: 0, // Reset to first page
        },
        expandedRowIds: new Set(), // Collapse all when data changes
      };

    case 'UPDATE_FILTER':
      return {
        ...state,
        filters: state.filters.map((filter, index) =>
          index === action.payload.index ? action.payload.filter : filter
        ),
        pagination: {
          ...state.pagination,
          pageIndex: 0, // Reset to first page
        },
        expandedRowIds: new Set(), // Collapse all when data changes
      };

    case 'REMOVE_FILTER':
      return {
        ...state,
        filters: state.filters.filter((_, index) => index !== action.payload),
        pagination: {
          ...state.pagination,
          pageIndex: 0, // Reset to first page
        },
        expandedRowIds: new Set(), // Collapse all when data changes
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: action.payload,
        pagination: {
          ...state.pagination,
          pageIndex: 0, // Reset to first page
        },
        expandedRowIds: new Set(), // Collapse all when data changes
      };

    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload,
        pagination: {
          ...state.pagination,
          pageIndex: 0, // Reset to first page
        },
        expandedRowIds: new Set(), // Collapse all when data changes
      };

    case 'TOGGLE_SORT': {
      const isSameField = state.sort?.field === action.payload.field;

      // Three-state cycle: unsorted → Desc → Asc → unsorted
      if (isSameField) {
        if (state.sort?.order === SortOrder.Asc) {
          // Third click: clear sort
          return {
            ...state,
            sort: null,
            pagination: {
              ...state.pagination,
              pageIndex: 0,
            },
            expandedRowIds: new Set(),
          };
        }
        // Second click: Desc → Asc
        return {
          ...state,
          sort: {
            field: action.payload.field,
            order: SortOrder.Asc,
          },
          pagination: {
            ...state.pagination,
            pageIndex: 0,
          },
          expandedRowIds: new Set(),
        };
      }

      // First click on new field: start with Desc (or provided order)
      return {
        ...state,
        sort: {
          field: action.payload.field,
          order: action.payload.order ?? SortOrder.Desc,
        },
        pagination: {
          ...state.pagination,
          pageIndex: 0,
        },
        expandedRowIds: new Set(),
      };
    }

    case 'CLEAR_SORT':
      return {
        ...state,
        sort: null,
        pagination: {
          ...state.pagination,
          pageIndex: 0, // Reset to first page
        },
        expandedRowIds: new Set(), // Collapse all when data changes
      };

    case 'SET_SEARCH':
      return {
        ...state,
        search: action.payload,
        pagination: {
          ...state.pagination,
          pageIndex: 0, // Reset to first page
        },
        expandedRowIds: new Set(), // Collapse all when data changes
      };

    case 'CLEAR_SEARCH':
      return {
        ...state,
        search: '',
        pagination: {
          ...state.pagination,
          pageIndex: 0, // Reset to first page
        },
        expandedRowIds: new Set(), // Collapse all when data changes
      };

    case 'SET_STATE':
      return {
        ...action.payload,
        pagination: {
          ...action.payload.pagination,
          pageIndex: 0, // Reset to first page
        },
      };

    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: [],
        pagination: {
          ...state.pagination,
          pageIndex: 0, // Reset to first page
        },
        expandedRowIds: new Set(), // Collapse all when data changes
      };

    case 'RESET':
      return initialTableQueryState;

    case 'TOGGLE_ROW_EXPANSION': {
      const rowId = action.payload;
      const next = new Set(state.expandedRowIds);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return {
        ...state,
        expandedRowIds: next,
      };
    }

    case 'COLLAPSE_ALL_ROWS':
      return {
        ...state,
        expandedRowIds: new Set(),
      };

    case 'SET_EXPANDED_ROW_IDS':
      return {
        ...state,
        expandedRowIds: action.payload,
      };

    default:
      return state;
  }
}
